import { LanguageCode, LocaleCode } from '@tink/config/src/markets';

export const validateString = (str: string | undefined, regex: RegExp): string | undefined => {
  if (str && str.match(regex)) {
    return str;
  }

  return undefined;
};

export const isString = (obj: any): obj is string => {
  return typeof obj === 'string' || obj instanceof String;
};

export const uuid = (): string => {
  let d = new Date().getTime();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);

    // eslint-disable-next-line no-bitwise
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// Eg. returns "en" from "en_US"
export const getLanguageCode = (locale: LocaleCode): LanguageCode => {
  return locale.substring(0, 2) as LanguageCode;
};

export const pickTextFromWithinDoubleQuotes = (text = '') => {
  if (text && text.includes('"')) {
    const parsed = /"(.*?)"/g.exec(text);

    if (parsed && parsed[1]) {
      return parsed[1]; // Only pick things in quotation marks.
    }
  }

  return text;
};

export const getEnumKeyByEnumValue = (theEnum: any, value: string) => {
  const key = Object.keys(theEnum).find((x) => theEnum[x] === value);

  return key || '';
};

// Checks that two objects have the same key-value pairs, ignoring order.
export const shallowEquals = (object1: Record<string, any>, object2: Record<string, any>) =>
  Object.keys(object1).every((key) => object1[key] === object2[key]);

// Masks the number so only 4 last numbers are visible
export const maskAccountNumber = (sourceAccount: string) => {
  const maskedSourceAccount = sourceAccount
    // Converting accountNumber to only be numbers
    .replace(/[^0-9]+/g, '')
    // Masking the accountNumber except for the 4 last digits
    .replace(/[0-9](?=([0-9]{4}))/g, '*');

  // Adding space after masking
  return `${maskedSourceAccount.slice(0, -4)} ${maskedSourceAccount.slice(-4)}`;
};

export const currencyCodeToSymbol = (code: string) => {
  const codes: Record<string, string> = {
    AED: 'د.إ.',
    AFN: '؋',
    ALL: 'L',
    AMD: 'դր',
    ANG: 'ƒ',
    AOA: 'Kz',
    ARS: '$',
    AUD: '$',
    AWG: 'ƒ',
    AZN: '₼',
    BAM: 'КМ',
    BBD: '$',
    BDT: '৳',
    BGN: 'лв.',
    BHD: 'د.ب.',
    BIF: 'FBu',
    BMD: '$',
    BND: '$',
    BOB: 'Bs.',
    BRL: 'R$',
    BSD: '$',
    BTN: 'Nu.',
    BWP: 'P',
    BYN: 'руб.',
    BZD: '$',
    CAD: '$',
    CDF: '₣',
    CHF: '₣',
    CKD: '$',
    CLP: '$',
    CNY: '¥元',
    COP: '$',
    CRC: '₡',
    CUC: '$',
    CUP: '₱',
    CVE: '$',
    CZK: 'Kč',
    DJF: 'ف.ج.',
    DKK: 'kr.',
    DOP: '$',
    DZD: 'د.ج.',
    EGP: 'ج.م.',
    EHP: 'Ptas.',
    ERN: 'ناكفا',
    ETB: 'ብር',
    EUR: '€',
    FJD: '$',
    FKP: '£',
    FOK: 'kr',
    GBP: '£',
    GEL: '₾',
    GGP: '£',
    GHS: '₵',
    GIP: '£',
    GMD: 'D',
    GNF: 'FG',
    GTQ: '$',
    GYD: '$',
    HKD: '$',
    HNL: 'L',
    HRK: 'kn',
    HTG: 'G',
    HUF: 'Ft',
    IDR: 'Rp',
    ILS: '₪',
    IMP: '£',
    INR: '₹',
    IQD: 'د.ع.',
    IRR: '﷼',
    ISK: 'kr',
    JEP: '£',
    JMD: '$',
    JOD: 'د.أ.',
    JPY: '¥',
    KES: 'KSh',
    KGS: 'с',
    KHR: '៛',
    KID: '$',
    KMF: 'CF',
    KPW: '₩',
    KRW: '₩',
    KWD: 'د.ك.',
    KYD: '$',
    KZT: '₸',
    LAK: '₭',
    LBP: 'ل.ل.',
    LKR: 'රු or ரூ',
    LRD: '$',
    LSL: 'L',
    LYD: 'ل.د.',
    MAD: 'د.م.',
    MDL: 'L',
    MGA: 'Ar',
    MKD: 'ден',
    MMK: 'Ks',
    MNT: '₮',
    MOP: 'MOP$',
    MRU: 'أ.م.',
    MUR: 'रु ',
    MVR: '.ރ',
    MWK: 'MK',
    MXN: '$',
    MYR: 'RM',
    MZN: 'MT',
    NAD: '$',
    NGN: '₦',
    NIO: 'C$',
    NOK: 'kr',
    NPR: 'रू',
    NZD: '$',
    OMR: 'ر.ع.',
    PAB: 'B/.',
    PEN: 'S/.',
    PGK: 'K',
    PHP: '₱',
    PKR: 'Rs',
    PLN: 'zł',
    PND: '$',
    PRB: 'р.',
    PYG: '₲',
    QAR: 'ر.ق.',
    RON: 'L',
    RSD: 'дин',
    RUB: '₽',
    RWF: 'R₣',
    SAR: 'ر.س.',
    SBD: '$',
    SCR: 'Rs',
    SDG: 'ج.س.',
    SEK: 'kr',
    SGD: '$',
    SHP: '£',
    SLL: 'Le',
    SLS: 'Sl',
    SOS: 'Ssh',
    SRD: '$',
    SSP: 'SS£',
    STN: 'Db',
    SVC: '₡',
    SYP: 'ل.س.',
    SZL: 'L',
    THB: '฿',
    TJS: 'SM',
    TMT: 'T',
    TND: 'د.ت.',
    TOP: 'PT',
    TRY: '₺',
    TTD: '$',
    TVD: '$',
    TWD: '圓',
    TZS: 'TSh',
    UAH: 'грн',
    UGX: 'Sh',
    USD: '$',
    UYU: '$',
    UZS: 'сум',
    VED: 'Bs.',
    VES: 'Bs.F',
    VND: '₫',
    VUV: 'VT',
    WST: 'ST',
    XAF: 'Fr.',
    XCD: '$',
    XOF: '₣',
    XPF: '₣',
    YER: 'ر.ي.',
    ZAR: 'R',
    ZMW: 'ZK',
    ZWB: '',
    ZWL: '$',
    Abkhazia: '',
    Artsakh: 'դր.',
  };

  if (codes[code]) {
    return codes[code];
  }

  return code;
};

export const getMajorVersion = (versionString: string) => {
  const majorVersionString = versionString.split('.')[0];
  try {
    return parseInt(majorVersionString, 10);
  } catch {
    return -1;
  }
};
